import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { fetchSubscriptions, updateSubscription } from 'store/subscriptions/actions';

import { DISCOUNT_TYPES } from 'constants/subscriptions';

import {
    OfferTypes,
    cancelFlow_offerResultScreenLoad,
    cancelFlow_offerResultClick,
} from 'services/analytics/trackers/cancelFlow';

import { useModals } from 'hooks';

import { getPriceFromCents } from 'helpers/prices';

import { IDiscountSubscription } from 'types/subscription';
import { ModalName } from 'types/modals';
import { selectSubscriptions } from 'store/subscriptions/selectors';

type PriceData = { currency: string; period: number; price: string };

type UseSpecialOfferModal = {
    isDiscountLoading: boolean;
    candidatePriceData: PriceData | null;
    discountPriceData: PriceData | null;
    discountSubscription: IDiscountSubscription | null;
    onUpdateSubscription: () => void;
};

const useSpecialOfferModal = (externalId: string): UseSpecialOfferModal => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openModal } = useModals();

    const { subscriptions, discountSubscription, isDiscountLoading } = useSelector(selectSubscriptions);

    const cancelCandidate = subscriptions.find(({ external_id }) => external_id === externalId)!;

    const candidatePriceData = cancelCandidate
        ? {
              currency: cancelCandidate.product.currency,
              period: cancelCandidate.subscription_period,
              price: cancelCandidate.product.price + '',
          }
        : null;

    const discountPriceData = discountSubscription
        ? {
              currency: discountSubscription!.currency,
              period: discountSubscription!.subscription_period,
              price: getPriceFromCents(discountSubscription!.amount) + '',
          }
        : null;

    const onUpdateSubscription = useCallback(() => {
        if (cancelCandidate && discountSubscription) {
            openModal(ModalName.WAITING_MODAL, { title: 'subscription.modals.updating' });

            dispatch(
                updateSubscription({
                    updatedSubscription: {
                        id: cancelCandidate.external_id,
                        product: discountSubscription,
                        discount_type: DISCOUNT_TYPES.base,
                        provider: cancelCandidate.payment_provider,
                    },
                    onSuccess: () => {
                        dispatch(fetchSubscriptions());

                        openModal(ModalName.SUCCESS_MODAL, {
                            icon: 'handsUp',
                            externalId: cancelCandidate.external_id,
                            offerType: OfferTypes.Discount_offer,
                            title: t('subscription.cancelFlow.specialOffer.discount.successModal.title'),
                            subtitle: t('subscription.cancelFlow.specialOffer.discount.successModal.subtitle', {
                                price: discountPriceData!.price,
                                period: discountPriceData!.period,
                                currencySign: discountPriceData!.currency,
                            }),
                        });
                    },
                    onError: () => {
                        openModal(ModalName.WARNING_MODAL, {
                            offerType: OfferTypes.Discount_offer,
                            onMainBtnClick: () => {
                                cancelFlow_offerResultClick({
                                    action: 'try_again',
                                    result: 'fail',
                                    subscription_id: cancelCandidate.external_id,
                                    offer_type: OfferTypes.Discount_offer,
                                });
                                onUpdateSubscription();
                            },
                            onSupportClick: () =>
                                cancelFlow_offerResultClick({
                                    action: 'try_again',
                                    result: 'fail',
                                    subscription_id: cancelCandidate.external_id,
                                    offer_type: OfferTypes.Discount_offer,
                                }),
                            title: t('subscription.cancelFlow.errorModal.title'),
                            analyticScreenLoadHandler() {
                                cancelFlow_offerResultScreenLoad({
                                    result: 'fail',
                                    offer_type: OfferTypes.Discount_offer,
                                    subscription_id: cancelCandidate?.external_id,
                                });
                            },
                        });
                    },
                })
            );
        }
    }, [cancelCandidate, discountSubscription]);

    return { discountSubscription, candidatePriceData, discountPriceData, isDiscountLoading, onUpdateSubscription };
};

export default useSpecialOfferModal;
